import React, { useState, useRef } from 'react';
import { 
  Sparkles, Download, Copy, Check, 
  AlertCircle, ArrowLeft, FileText, Trees,
  Shield, BookOpen, RefreshCw, Feather, Camera,
  Image as ImageIcon, X, Eye, Flame, Coffee, Heart, Zap
} from 'lucide-react';

interface FolkHealerAgentViewProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
}

interface AttachedFile {
  name: string;
  text: string;
}

interface PhotoItem {
  name: string;
  dataUrl: string; // base64 data URL
  mimeType: string;
}

const LOADING_STEPS_UK = [
  "Ініціалізація етнографічного та цілительського ШІ-комплексу...",
  "ШІ-Ядро Gemini 3.7 Flash & Vision проводить зоровий аналіз фото (воску / кавової гущі / трав)...",
  "Знахар-Травник аналізує властивості рослин, цілющі збори та знахарські рецепти...",
  "Етнограф досліджує народні прикмети, обряди та традиційний календар...",
  "Майстер ворожінь тлумачить силуети, традиційну символіку передбачень та замовляння...",
  "Редактор узагальнює висновки та будує підсумковий звіт для експорту в Word (.docx)..."
];

const LOADING_STEPS_PL = [
  "Inicjalizacja modułu etnografii i zielarstwa tradycyjnego...",
  "Wizualna analiza przesłanych zdjęć (wosk / fusy kawowe / zioła)...",
  "Zielarz analizuje właściwości fitoterapeutyczne i tradycyjne receptury...",
  "Etnograf bada tradycyjne wierzenia, obrzędy i kalendarz ludowy...",
  "Interpretacja symboliki wizualnej, wróżb i przysłów ludowych...",
  "Synteza wniosków i generowanie raportu Word (.docx)..."
];

const LOADING_STEPS_EN = [
  "Initializing ethnography and folk herbal medicine engine...",
  "Gemini 3.7 Flash & Vision visual analysis of uploaded photos...",
  "Herbalist analyzing botanical properties and traditional recipes...",
  "Ethnographer researching folk omens, traditions, and lunar calendar...",
  "Divination master interpreting visual patterns and traditional symbolism...",
  "Synthesizing findings and formatting Word (.docx) dossier..."
];

export default function FolkHealerAgentView({ language, onBack, onGoHome }: FolkHealerAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const [category, setCategory] = useState(
    isPl ? 'Analiza kompleksowa (Zioła + Wierzenia + Symbole)' : isUk ? 'Комплексний аналіз (Трави + Прикмети + Ворожіння)' : 'Comprehensive Analysis (Herbs + Omens + Divination)'
  );
  const [taskDetailsText, setTaskDetailsText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [isReadingFiles, setIsReadingFiles] = useState(false);
  const [isShortAnswer, setIsShortAnswer] = useState(false);

  // Live Camera State
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  const [visualAnalysisText, setVisualAnalysisText] = useState<string | null>(null);

  const [copied, setCopied] = useState(false);
  const [isDownloadingDocx, setIsDownloadingDocx] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isProcessing) {
      setLoadingStepIndex(0);
      const steps = isPl ? LOADING_STEPS_PL : isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing, isUk, isPl]);

  // Open Live Camera
  const handleStartCamera = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setCameraStream(stream);
      setIsCameraOpen(true);

      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err: any) {
      console.error("Camera access error:", err);
      setError(isUk ? "Не вдалося отримати доступ до веб-камери. Перевірте дозволи браузера або завантажте фото з файлу." : "Could not access camera. Please check browser permissions or upload an image file.");
    }
  };

  const handleStopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraOpen(false);
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

    const newPhoto: PhotoItem = {
      name: `Photo_${Date.now()}.jpg`,
      dataUrl,
      mimeType: 'image/jpeg'
    };

    setPhotos(prev => [...prev, newPhoto]);
    handleStopCamera();
  };

  // Image Upload Handler
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;

      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setPhotos(prev => [
            ...prev,
            {
              name: file.name,
              dataUrl: event.target!.result as string,
              mimeType: file.type || 'image/jpeg'
            }
          ]);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // File Upload Handler (PDF, DOCX, TXT)
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsReadingFiles(true);
    const newAttached: AttachedFile[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        // Also add to photos if image file selected here
        const reader = new FileReader();
        reader.onload = (evt) => {
          if (evt.target?.result) {
            setPhotos(p => [...p, { name: file.name, dataUrl: evt.target!.result as string, mimeType: file.type }]);
          }
        };
        reader.readAsDataURL(file);
        continue;
      }

      try {
        const text = await file.text();
        newAttached.push({
          name: file.name,
          text: text.slice(0, 50000)
        });
      } catch (err) {
        console.error("Failed to read file", file.name, err);
      }
    }

    setAttachedFiles(prev => [...prev, ...newAttached]);
    setIsReadingFiles(false);
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Demo presets
  const handleDemoWaxCasting = () => {
    setCategory(isUk ? 'Традиційні Народні Ворожіння & Символіка' : 'Folk Divination & Symbolism');
    setTaskDetailsText(
      isUk
        ? `ВІЗУАЛЬНЕ ВОРОЖІННЯ НА ВОСКУ (ВИЛИВКИ ВОСКУ У ДЖЕРЕЛЬНУ ВОДУ):

Опис проведеного обряду:
Виливання розтопленого бджолиного воску проводилося на свято Андрія Первозванного в миску з холодною джерельною водою. 
На фотографії виливку зафіксовано воскову фігуру:
- У центрі чітко проглядається силует птаха з розправленими крилами, наче сокіл чи голубка.
- Збоку від основного виливку утворилися дві невеличкі воскові краплі-вузлики та тонке закручене воскове кільце.
- Краї фігури рівні, без гострих голок, вісок світло-жовтого натурального кольору.

Проведіть детальний аналіз за народними віруваннями та тлумаченнями воскових фігур:
1. Що символізує силует птаха та воскове кільце для долі, кохання та фінансового стану?
2. Тлумачення бічних крапель та форми воску.
3. Наведіть старовинне народне замовляння-допрошення на щасливу долю та захист.`
        : `WAX CASTING DIVINATION ANALYSIS:
Wax poured into spring water showing bird silhouette and wax ring. Provide traditional folk interpretations and blessing charms.`
    );
    setError(null);
  };

  const handleDemoCoffeeGrounds = () => {
    setCategory(isUk ? 'Традиційні Народні Ворожіння & Символіка' : 'Folk Divination & Symbolism');
    setTaskDetailsText(
      isUk
        ? `ВОРІЖІННЯ НА КАВОВІЙ ГУЩІ В ЧАШЦІ:

Опис візерунків кавового осаду:
- На верхньому краї чашки біля ручки чітко видно силует підкови ріжками вгору.
- На дні чашки сформувалася темна довга смуга (дорога), поруч із якою проглядаються три виразні крапки та фігура корони.
- На бічній стінці чашки гуща лягла у формі гіллястого дерева з міцним стовбуром.

Проведіть етнографічно-символічне розшифрування знаків:
1. Значення підкови, дороги з крапками та гіллястого дерева.
2. Що чекає у найближчий місяць у справах, подорожах та родині?
3. Традиційні народні поради для збереження добробуту.`
        : `COFFEE GROUNDS READING:
Grounds forming horseshoe, road with dots, and branching tree silhouettes. Interpret upcoming opportunities and family harmony.`
    );
    setError(null);
  };

  const handleDemoHerbs = () => {
    setCategory(isUk ? 'Травництво & Фітотерапія (Зелейництво)' : 'Herbalism & Phytotherapy');
    setTaskDetailsText(
      isUk
        ? `ЗНАХАРСЬКЕ ТРАВНИЦТВО ТА ЗЕЛЕЙНИЦТВО (ЗБІР ЗДОРОВ'Я):

Опис трав'яного збору:
- Склад: ромашка аптечна (квітки), матерінка звичайна, чебрець повзучий, липовий цвіт та листя суниці лісової.
- Мета: зміцнення імунітету, заспокоєння нервової системи, лікування застуди та сезонної втоми.

Потрібно надати:
1. Детальний ботанічний та народний опис кожної рослини.
2. Традиційні знахарські рецепти приготування відварів, цілющих настоїв та запарювання в термосі.
3. Правила зберігання та правила збору трав за народним календарем.
4. ⚠️ Суворі застереження щодо протипоказань та дозування.`
        : `HERBAL MEDICINE PREPARATION:
Herbal blend of chamomile, oregano, thyme, linden flower, and wild strawberry leaves. Provide recipes, storage rules, and health warnings.`
    );
    setError(null);
  };

  // Execute Analysis Workflow
  const handleRunAnalysis = async () => {
    if (!taskDetailsText.trim() && photos.length === 0) {
      setError(isUk ? "Будь ласка, введіть запитання або зробіть/завантажте фото." : "Please enter your question or attach a photo.");
      return;
    }

    setIsProcessing(true);
    setError(null);
    setReport(null);
    setVisualAnalysisText(null);

    let visualAnalysis = "";

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (customKey) headers['x-api-key'] = customKey;

      // Step 1: Run Gemini Vision Analysis if photos present
      if (photos.length > 0) {
        const visionRes = await fetch('/api/admin/folk-healer-vision', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            images: photos.map(p => ({ data: p.dataUrl, mimeType: p.mimeType })),
            prompt: taskDetailsText || "Проведіть зоровий аналіз фотографії (воску, кавової гущі, трав тощо).",
            isShortAnswer
          })
        });

        if (visionRes.ok) {
          const visionData = await visionRes.json();
          visualAnalysis = visionData.visualAnalysis || "";
          setVisualAnalysisText(visualAnalysis);
        }
      }

      // Build full details text including visual analysis
      let fullText = taskDetailsText;

      if (isShortAnswer) {
        fullText += `\n\n⚠️ [СУВОРЕ ПРОМПТ-ПОБЕЖЕННЯ: КОРОТКА ВІДПОВІДЬ]:
Користувач увімкнув галочку "Коротка відповідь".
Сформуй звіт МАКСИМАЛЬНО КОРОТКО, СТИСЛО ТА ПО СУТІ.
АБСОЛЮТНО БЕЗ ВСТУПНИХ СЛІВ, ІСТОРІЧНИХ ВСТУПІВ ТА ЗАГАЛЬНИХ ФРАЗ!
Надай відповідь ВИКЛЮЧНО за двома чіткими пунктами:
1. 🎯 **ЩО ЦЕ ОЗНАЧАЄ (Суть та значення знаку/фігури/рецепту)**
2. 💡 **ЩО РОБИТИ (Конкретні практичні дії, поради чи поради для обряду)**`;
      }

      if (visualAnalysis) {
        fullText += `\n\n[ВІЗУАЛЬНИЙ АНАЛІЗ ФОТОГРАФІЇ ВІД GEMINI 3.6 VISION]:\n${visualAnalysis}`;
      }

      if (attachedFiles.length > 0) {
        const filesContext = attachedFiles
          .map(f => `--- ЗМІСТ ФАЙЛУ: ${f.name} ---\n${f.text}`)
          .join("\n\n");
        fullText += `\n\n[ДОДАТКОВІ МАТЕРІАЛИ ТА ТЕКСТИ ФАЙЛІВ]:\n${filesContext}`;
      }

      // Step 2: Run Multi-Agent Crew
      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: "Народне цілительство, знахарство, травництво, прикмети та ворожіння",
          agentId: "folk-healer-traditions",
          inputs: {
            category_or_topic: category,
            task_details: fullText
          }
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка аналізу аналітичною системою.' : 'Failed to execute folk healer analysis.'));
      }

      const data = await response.json();

      if (data.jobId) {
        const pollJob = async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
            if (!statusRes.ok) throw new Error(isUk ? "Помилка перевірки статусу." : "Status check failed.");
            
            const jobData = await statusRes.json();
            if (jobData.status === "completed") {
              setReport(jobData.report);
              setIsProcessing(false);
            } else if (jobData.status === "failed") {
              throw new Error(jobData.error || (isUk ? "Помилка виконання завдання." : "Job failed."));
            } else {
              setTimeout(pollJob, 3000);
            }
          } catch (pollErr: any) {
            setError(pollErr.message);
            setIsProcessing(false);
          }
        };
        setTimeout(pollJob, 3000);
      } else if (data.report) {
        setReport(data.report);
        setIsProcessing(false);
      } else {
        throw new Error(isUk ? 'Отримано порожню відповідь від сервера.' : 'Empty response from server.');
      }
    } catch (err: any) {
      console.error("Folk healer agent execution error:", err);
      setError(err.message || (isUk ? 'Сталася помилка при запуску аналізу.' : 'An error occurred during analysis.'));
      setIsProcessing(false);
    }
  };

  const handleCopyReport = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadDocx = async () => {
    if (!report) return;
    setIsDownloadingDocx(true);

    try {
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: report,
          fileName: `Folk_Healing_Traditions_${new Date().toISOString().slice(0, 10)}`
        })
      });

      if (!response.ok) throw new Error("Docx generation failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Folk_Healing_Traditions_${new Date().toISOString().slice(0, 10)}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(isUk ? "Помилка формування файлу Word (.docx)" : "Failed to download Word (.docx) document");
    } finally {
      setIsDownloadingDocx(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 pb-12 text-slate-800 font-mono animate-fade-in">
      {/* Hidden Canvas for Camera Snapshot */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Top Navigation Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <div className="flex items-center gap-3">
          <button 
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700 font-mono"
          >
            🏠 {isPl ? "Strona Główna" : isUk ? "На головну" : "Home"}
          </button>
          <button 
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700 font-mono"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isPl ? "← Powrót do Centrum Admina" : isUk ? "← До Адмін-Центру" : "← Back to Admin Hub"}</span>
          </button>
          <div className="flex items-center gap-2 px-2.5 py-1 bg-amber-500/20 text-amber-300 border border-amber-400/30 rounded-md text-[10px] font-extrabold uppercase">
            <Shield className="w-3.5 h-3.5 text-amber-400" />
            <span>{isPl ? "🛡️ Tylko dla administratorów" : isUk ? "🛡️ Тільки для адміністраторів" : "🛡️ Admin Only"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-purple-500/20 text-purple-300 border border-purple-400/30 rounded-md flex items-center gap-1">
            <Eye className="w-3.5 h-3.5 text-purple-400" />
            Gemini 3.7 Flash & Vision & Multi-Agent
          </span>
        </div>
      </div>

      {/* Main Card */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-xl space-y-6">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-6 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="p-3.5 rounded-xl bg-amber-50 text-amber-600 border border-amber-200/80">
              <Feather className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 uppercase tracking-tight flex items-center gap-2">
                <span>{isPl ? "Ekspert Ziołolecznictwa, Etnografii i Tradycji" : isUk ? "Експерт Травництва, Етнографії & Традицій" : "Folk Healing, Herbalism & Traditions Oracle"}</span>
              </h1>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl font-mono">
                {isPl
                  ? "Analiza na podstawie zdjęć (odlewy wosku, fusy z kawy, zioła, dłonie) oraz zapytań tekstowych z eksportem do Word (.docx)."
                  : isUk 
                  ? "Аналіз за фотографією (виливки воску, кавова гуща, трави, долоні) та текстовими запитами з формуванням звіту у Word (.docx)." 
                  : "AI photo analysis (wax casting, coffee grounds, herbs, palms) and custom inquiries with Word (.docx) export."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleDemoWaxCasting}
              className="px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300/80 font-bold text-[11px] uppercase rounded-lg transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
            >
              <Flame className="w-3.5 h-3.5 text-amber-600" />
              <span>{isPl ? "🕯️ Wosk / Wróżby" : isUk ? "🕯️ Ворожіння на воску" : "🕯️ Wax Casting"}</span>
            </button>
            <button
              type="button"
              onClick={handleDemoCoffeeGrounds}
              className="px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300/80 font-bold text-[11px] uppercase rounded-lg transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
            >
              <Coffee className="w-3.5 h-3.5 text-amber-600" />
              <span>{isPl ? "☕ Fusy z kawy" : isUk ? "☕ Кавова гуща" : "☕ Coffee Grounds"}</span>
            </button>
            <button
              type="button"
              onClick={handleDemoHerbs}
              className="px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300/80 font-bold text-[11px] uppercase rounded-lg transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
            >
              <Trees className="w-3.5 h-3.5 text-amber-600" />
              <span>{isPl ? "🌿 Mieszanka ziół" : isUk ? "🌿 Трав'яний збір" : "🌿 Herbs"}</span>
            </button>
          </div>
        </div>

        {/* Live Camera Modal */}
        {isCameraOpen && (
          <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-lg w-full text-white space-y-4 shadow-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Camera className="w-5 h-5 text-amber-400" />
                  <h3 className="text-sm font-bold uppercase tracking-wider text-amber-300">
                    {isUk ? "Камера: Сфотографувати виливок воску / гущу / трави" : "Live Camera Capture"}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={handleStopCamera}
                  className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="relative aspect-video bg-black rounded-xl overflow-hidden border border-slate-800 flex items-center justify-center">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleStopCamera}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors"
                >
                  {isUk ? "Скасувати" : "Cancel"}
                </button>
                <button
                  type="button"
                  onClick={handleCapturePhoto}
                  className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white font-extrabold text-xs uppercase rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-amber-600/30"
                >
                  <Camera className="w-4 h-4" />
                  <span>{isUk ? "📸 Зробити знімок" : "📸 Capture Photo"}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Input Form */}
        {!report && !isProcessing && (
          <div className="space-y-5">
            <div>
              <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wide mb-2">
                {isUk ? "🎯 Напрямок / Категорія запиту" : "🎯 Category / Topic Focus"}
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full p-2.5 bg-slate-50 border border-slate-300 focus:border-amber-500 focus:outline-none text-xs rounded-lg font-mono text-slate-900 transition-colors"
              >
                <option value={isUk ? "Комплексний аналіз (Трави + Прикмети + Ворожіння)" : "Comprehensive Analysis (Herbs + Omens + Divination)"}>
                  {isUk ? "🌿🔮 Комплексний етно-аналіз (Трави + Знахарство + Прикмети + Ворожіння)" : "Comprehensive Ethno Analysis (Herbs + Healing + Omens + Divination)"}
                </option>
                <option value={isUk ? "Народне Цілительство & Знахарські Рецепти" : "Folk Healing & Herbal Recipes"}>
                  {isUk ? "🌱 Народне Цілительство & Знахарські Рецепти" : "Folk Healing & Herbal Recipes"}
                </option>
                <option value={isUk ? "Травництво & Фітотерапія (Зелейництво)" : "Herbalism & Phytotherapy"}>
                  {isUk ? "🌿 Травництво & Фітотерапія (Зелейництво)" : "Herbalism & Phytotherapy"}
                </option>
                <option value={isUk ? "Народні Прикмети, Обряди & Календарні Свята" : "Folk Omens & Calendar Rites"}>
                  {isUk ? "☀️ Народні Прикмети, Обряди & Календарні Свята" : "Folk Omens & Calendar Rites"}
                </option>
                <option value={isUk ? "Традиційні Народні Ворожіння & Символіка" : "Folk Divination & Symbolism"}>
                  {isUk ? "🔮 Традиційні Народні Ворожіння & Символіка" : "Folk Divination & Symbolism"}
                </option>
              </select>
            </div>

            {/* Photo Capture & Photo Gallery Block */}
            <div className="space-y-3 p-4 bg-amber-50/50 border border-amber-200/80 rounded-xl">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="text-xs font-extrabold text-amber-900 uppercase tracking-wider flex items-center gap-2">
                  <Camera className="w-4 h-4 text-amber-600" />
                  <span>{isUk ? "📷 СФОТОГРАФУВАТИ АБО ЗАВАНТАЖИТИ ФОТО (Ворожіння на воску, кавова гуща, трави)" : "📷 TAKE PHOTO OR UPLOAD (Wax casting, coffee grounds, herbs)"}</span>
                </label>
                <span className="text-[10px] text-amber-800 font-bold">
                  {isUk ? "ШІ Gemini 3.7 Flash & Vision проведе зоровий розбір" : "Gemini 3.7 Flash & Vision visual analysis"}
                </span>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={handleStartCamera}
                  className="px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-extrabold text-xs uppercase rounded-lg transition-colors flex items-center gap-2 shadow-xs cursor-pointer"
                >
                  <Camera className="w-4 h-4" />
                  <span>{isUk ? "📷 Сфотографувати з камери" : "📷 Take Photo from Camera"}</span>
                </button>

                <label className="px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 rounded-lg font-bold text-xs uppercase cursor-pointer transition-colors flex items-center gap-2 shadow-2xs">
                  <ImageIcon className="w-4 h-4 text-amber-600" />
                  <span>{isUk ? "🖼️ Завантажити фото з файлу" : "🖼️ Upload Image File"}</span>
                  <input
                    type="file"
                    multiple
                    onChange={handlePhotoUpload}
                    accept="image/*"
                    className="hidden"
                  />
                </label>

                <label className="px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-lg font-bold text-xs uppercase cursor-pointer transition-colors flex items-center gap-2 shadow-2xs">
                  <FileText className="w-4 h-4 text-slate-600" />
                  <span>{isReadingFiles ? (isUk ? "Зчитування..." : "Reading...") : (isUk ? "📄 Додати документи (PDF, DOCX, TXT)" : "📄 Add Docs")}</span>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    accept=".pdf,.docx,.doc,.txt"
                    className="hidden"
                    disabled={isReadingFiles}
                  />
                </label>
              </div>

              {/* Photo Preview Thumbnails */}
              {photos.length > 0 && (
                <div className="pt-2">
                  <div className="text-[11px] font-bold text-amber-950 mb-2 uppercase">
                    {isUk ? "Приєднані фотографії для ШІ-аналізу:" : "Attached photos for AI vision analysis:"}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {photos.map((photo, idx) => (
                      <div key={idx} className="relative group border border-amber-300 rounded-lg overflow-hidden bg-white shadow-2xs">
                        <img src={photo.dataUrl} alt={photo.name} className="w-full h-24 object-cover" />
                        <button
                          type="button"
                          onClick={() => handleRemovePhoto(idx)}
                          className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-90 hover:opacity-100 transition-opacity cursor-pointer shadow-xs"
                          title={isUk ? "Видалити фото" : "Remove photo"}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                        <div className="p-1.5 text-[9px] font-mono text-slate-600 truncate bg-slate-50 border-t border-slate-100">
                          {photo.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Attached documents list */}
              {attachedFiles.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap pt-1">
                  {attachedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 bg-white text-slate-800 border border-slate-300 px-3 py-1.5 rounded-lg text-xs font-bold font-mono shadow-2xs">
                      <BookOpen className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                      <span className="truncate max-w-[200px]">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(idx)}
                        className="p-0.5 hover:bg-slate-100 rounded text-slate-600 hover:text-red-600 transition-colors cursor-pointer ml-1"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Task Details text area */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">
                {isUk ? "📝 ОПИС ЗАПИТАННЯ, ТЕМИ АБО ОПИС ДЛЯ ПРОВЕДЕННЯ АНАЛІЗУ" : "📝 QUESTION DETAILS & SPECIFICATIONS"}
              </label>
              <textarea
                rows={6}
                value={taskDetailsText}
                onChange={(e) => setTaskDetailsText(e.target.value)}
                placeholder={
                  isUk
                    ? "Введіть ваші запитання про цілющі трави, рецепти відварів, народні прикмети, ворожіння або поясніть що саме сфотографовано (наприклад: 'фото виливку воску у воду')..."
                    : "Enter your question or describe the attached photo (e.g. 'wax casting photo poured into spring water')..."
                }
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 focus:border-amber-500 focus:bg-white rounded-lg text-xs font-mono text-slate-900 transition-colors leading-relaxed"
              />
            </div>

            {/* Short Concise Answer Checkbox */}
            <label className="flex items-center gap-3 p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-xl cursor-pointer hover:bg-amber-500/20 transition-all select-none">
              <input
                type="checkbox"
                checked={isShortAnswer}
                onChange={(e) => setIsShortAnswer(e.target.checked)}
                className="w-4 h-4 text-amber-600 rounded border-amber-400 focus:ring-amber-500 cursor-pointer"
              />
              <div className="flex flex-col">
                <span className="text-xs font-extrabold text-amber-950 uppercase tracking-wide flex items-center gap-1.5">
                  <Zap className="w-4 h-4 text-amber-600 fill-amber-600 shrink-0" />
                  <span>{isUk ? "⚡ Надати КОРОТКУ відповідь (без вступів, тільки суть: що означає та що робити)" : "⚡ Provide SHORT answer (no intros, essence & action steps only)"}</span>
                </span>
                <span className="text-[10px] text-amber-900/80 font-mono">
                  {isUk ? "ШІ дасть стислу відповідь виключно за 2 пунктами: 1) 🎯 Що це означає (суть); 2) 💡 Що робити (поради/дії)." : "AI will omit long background intros and output 2 clear points: 1) Meaning; 2) Actions to take."}
                </span>
              </div>
            </label>
          </div>
        )}

        {/* Error notification */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-mono flex items-center gap-3">
            <AlertCircle className="w-5 h-5 shrink-0 text-red-500" />
            <span>{error}</span>
          </div>
        )}

        {/* Bottom Actions */}
        {!report && !isProcessing && (
          <div className="pt-4 border-t border-slate-100 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span>{isUk ? "Результат за ДСТУ 4163:2020 з можливістю завантаження Word (.docx)." : "DSTU 4163 report layout with Word (.docx) export."}</span>
            </div>
            <button
              onClick={handleRunAnalysis}
              disabled={isProcessing}
              className="px-6 py-3.5 bg-amber-600 hover:bg-amber-700 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-amber-600/30 flex items-center gap-2.5 cursor-pointer disabled:opacity-50"
            >
              <span>{isUk ? "🚀 Провести Візуальний & Мультиагентний Аналіз" : "🚀 Run Vision & Multi-Agent Analysis"}</span>
            </button>
          </div>
        )}

        {/* Loading state animation */}
        {isProcessing && (
          <div className="p-6 bg-slate-900 border border-slate-800 rounded-xl text-white space-y-4 animate-pulse">
            <div className="flex items-center gap-3">
              <RefreshCw className="w-5 h-5 animate-spin text-amber-400" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-amber-300">
                {isUk ? "ШІ-Ядро Gemini 3.7 Flash / Vision опрацьовує ваш запит" : "Gemini 3.7 Flash / Vision processing inquiry"}
              </h3>
            </div>
            <p className="text-xs text-slate-300 font-mono">
              {(isPl ? LOADING_STEPS_PL : isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN)[loadingStepIndex]}
            </p>
          </div>
        )}

        {/* Report Output & Docx Download */}
        {report && (
          <div className="pt-6 border-t border-slate-200 space-y-4 animate-fade-in">
            <div className="flex items-center justify-between flex-wrap gap-3 bg-slate-900 p-4 rounded-xl text-white">
              <div className="flex items-center gap-2">
                <Feather className="w-5 h-5 text-amber-400" />
                <h3 className="text-sm font-bold uppercase tracking-wide text-amber-300">
                  {isUk ? "ЗВІТ З НАРОДНОГО ЦІЛИТЕЛЬСТВА ТА ТРАДИЦІЙ" : "FOLK HEALING & TRADITIONS REPORT"}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyReport}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer border border-slate-700"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copied ? (isUk ? "Скопійовано" : "Copied") : (isUk ? "Копіювати" : "Copy")}</span>
                </button>
                <button
                  onClick={handleDownloadDocx}
                  disabled={isDownloadingDocx}
                  className="px-4 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-extrabold uppercase rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer shadow-xs disabled:opacity-50"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>{isDownloadingDocx ? (isUk ? "Формування Word..." : "Generating...") : (isUk ? "Завантажити Word (.docx)" : "Download Word (.docx)")}</span>
                </button>
              </div>
            </div>

            {visualAnalysisText && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-950 text-xs font-mono space-y-2">
                <div className="font-extrabold flex items-center gap-2 text-amber-900">
                  <Eye className="w-4 h-4 text-amber-600" />
                  <span>{isUk ? "РЕЗУЛЬТАТИ ВІЗУАЛЬНОГО АНАЛІЗУ ФОТО ВІД GEMINI 3.6 VISION:" : "GEMINI 3.6 VISION PHOTO INSPECTION:"}</span>
                </div>
                <div className="whitespace-pre-wrap leading-relaxed text-slate-800">
                  {visualAnalysisText}
                </div>
              </div>
            )}

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-xs text-slate-800 font-mono leading-relaxed overflow-x-auto whitespace-pre-wrap">
              {report}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
